import { useEffect, useRef, useState, useMemo } from "react";
import {
  ValidateData,
  getPortfolioPropertySummary,
  Property,
} from "./FetchProperties";
import { updateSearchParam } from "@/utils/propertyUtils";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useParams, useLocation } from "react-router-dom";
import {
  setSelectedProperty,
  clearSelectedProperty,
  selectSelectedProperty,
  setSelectedMultiProperties,
  clearSelectedMultiProperties,
  markPortfolioAutoSelected,
  selectHasAutoSelectedPortfolio,
  selectSelectedMultiProperties,
  type MultiSelectPage,
} from "@/redux/slices/property";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useIsInternalUser } from "@/hooks/useIsInternalUser";
import SingleSelectPropertyPanel from "./SingleSelectPropertyPanel";
import MultiSelectDesktopDropdown from "./MultiSelectDesktopDropdown";
import MultiSelectMobileSheet from "./MultiSelectMobileSheet";
import { SelectionLimitWarning } from "./PropertyListRows";
import { trackActiveLeadsEvent, ACTIVE_LEADS_EVENTS } from "@/lib/posthog";

type SingleSelectProps = {
  multiSelect?: false;
  onPropertySelect: (propertyId: string) => void;
  darkBackground?: never;
  page?: never;
};

type MultiSelectProps = {
  multiSelect: true;
  // Which tab this selector is on — drives analytics and the portfolio
  // auto-select opt-in. The selection itself is shared across these pages.
  page: MultiSelectPage;
  onPropertySelect: (propertyIds: string[]) => void;
  darkBackground?: boolean;
};

type Props = SingleSelectProps | MultiSelectProps;

const MAX_PROPERTIES = 15;

// Pages that pre-select the user's portfolio when the shared selection is still
// empty. GPC/GPRT don't trigger it, but show it once another page has.
const AUTO_SELECT_PORTFOLIO_PAGES: ReadonlySet<MultiSelectPage> =
  new Set<MultiSelectPage>(["activeLeads", "pricingAudit"]);

// Falls back to the legacy singular `propertyId` param when `propertyIds` is
// absent, so deep links created before a route switched from single- to
// multi-select still restore.
const parsePropertyIds = (params: URLSearchParams): string[] =>
  params.get("propertyIds")?.split(",").filter(Boolean) ??
  (params.get("propertyId") ? [params.get("propertyId") as string] : []);

const sortWithSelectedFirst = (
  properties: Property[],
  selectedProperties: Property[]
): Property[] => {
  const selectedIds = new Set(selectedProperties.map((p) => p.propertyId));
  return [...properties].sort(
    (a, b) =>
      (selectedIds.has(a.propertyId) ? 0 : 1) -
      (selectedIds.has(b.propertyId) ? 0 : 1)
  );
};

const PropertySelector = ({
  onPropertySelect,
  multiSelect = false,
  darkBackground = false,
  page,
}: Props) => {
  const dispatch = useDispatch();
  const isInternal = useIsInternalUser();
  const multiSelectPage = page as MultiSelectPage;
  // This component is shared with GPC — analytics below are scoped to Active Leads only.
  const isActiveLeadsMultiSelect =
    multiSelect && multiSelectPage === "activeLeads";
  const selectedProperty = useSelector(selectSelectedProperty);
  const hasAutoSelectedPortfolio = useSelector(selectHasAutoSelectedPortfolio);
  const storedSelectionRef = useRef<Property[]>([]);
  storedSelectionRef.current = useSelector(selectSelectedMultiProperties);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const location = useLocation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);
  const hasFetched = useRef(false);
  const { showSnackbar } = useSnackbar();
  const [portfolioPropertyIds, setPortfolioPropertyIds] = useState<Set<string>>(
    new Set()
  );

  // Lifted here (not in children) because resizing mid-interaction swaps which variant renders
  // without remounting — children would drift and an in-progress selection would reset on resize.
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [stableProperties, setStableProperties] = useState<Property[]>([]);

  const isOnInstancePage = Boolean(params.instanceid);
  // Routes where ?propertyId= names the record being viewed, not a selection.
  // parsePropertyIds falls back to that singular param for legacy deep links,
  // so on these routes it would otherwise collapse a multi-selection down to
  // the one property being viewed — and lose it on the way back.
  const isPerPropertyRoute =
    isOnInstancePage || location.pathname.includes("/property-level-configs");

  const propertyIdFromUrl = multiSelect ? null : searchParams.get("propertyId");
  const propertyIdsFromUrl = multiSelect ? parsePropertyIds(searchParams) : [];

  useEffect(() => {
    if (hasFetched.current) return;

    const fetchProperties = async () => {
      hasFetched.current = true;
      setLoading(true);
      try {
        let props: Property[];
        let portfolioPropertySummaries: Property[] = [];

        // Both modes group by portfolio now, so they share one fetch.
        const fetched = await getPortfolioPropertySummary();
        portfolioPropertySummaries = fetched?.portfolioPropertySummaries ?? [];
        // External users see their portfolio and nothing else. The endpoint
        // already omits nonPortfolioPropertySummaries for them, but the list is
        // built explicitly here so a future response shape can't quietly widen
        // what a customer is shown.
        props = isInternal
          ? [
              ...portfolioPropertySummaries,
              ...(fetched?.nonPortfolioPropertySummaries ?? []),
            ]
          : [...portfolioPropertySummaries];
        setPortfolioPropertyIds(
          new Set(portfolioPropertySummaries.map((p) => p.propertyId))
        );

        // Internal only: ValidateData returns the entire estate with an empty
        // portfolio set, so falling back to it for an external user whose
        // portfolio came back empty would list every property in the business
        // under "Other Properties". An external user with no portfolio gets an
        // empty picker, which is the truthful state.
        if (isInternal && props.length === 0) {
          props = await ValidateData();
          setPortfolioPropertyIds(new Set());
        }

        setProperties(props);

        if (multiSelect) {
          // Re-read the URL directly instead of trusting the `propertyIdsFromUrl`
          // closure: this callback only ever runs once (guarded by hasFetched),
          // so a stale closure could otherwise ignore a propertyIds param that
          // showed up after mount (e.g. a sibling restore-from-Redux effect, or
          // browser back/forward) and clobber it with the auto-select fallback.
          const latestParams = new URLSearchParams(window.location.search);
          const currentIds = parsePropertyIds(latestParams);

          if (currentIds.length > 0) {
            const matched = currentIds
              .map((id) => props.find((p) => p.propertyId === id))
              .filter((p): p is Property => p !== undefined);
            setSelectedProperties(matched);
            if (matched.length > 0) {
              dispatch(setSelectedMultiProperties(matched));
              (onPropertySelect as (ids: string[]) => void)(
                matched.map((p) => p.propertyId)
              );
            }
          } else if (storedSelectionRef.current.length > 0) {
            const restored = storedSelectionRef.current
              .map((stored) =>
                props.find((p) => p.propertyId === stored.propertyId)
              )
              .filter((p): p is Property => p !== undefined);
            setSelectedProperties(restored);
            if (restored.length > 0) {
              (onPropertySelect as (ids: string[]) => void)(
                restored.map((p) => p.propertyId)
              );
            }
          } else if (
            AUTO_SELECT_PORTFOLIO_PAGES.has(multiSelectPage) &&
            !hasAutoSelectedPortfolio &&
            portfolioPropertySummaries.length > 0
          ) {
            const autoSelected = portfolioPropertySummaries.slice(
              0,
              MAX_PROPERTIES
            );
            setSelectedProperties(autoSelected);
            dispatch(setSelectedMultiProperties(autoSelected));
            dispatch(markPortfolioAutoSelected());
            (onPropertySelect as (ids: string[]) => void)(
              autoSelected.map((p) => p.propertyId)
            );
            setSearchParams((prev) =>
              updateSearchParam(
                prev,
                "propertyIds",
                autoSelected.map((p) => p.propertyId).join(",")
              )
            );
          }
          setLoading(false);
        } else {
          let propertyToSelect: Property | null = null;

          if (propertyIdFromUrl) {
            propertyToSelect =
              props.find((p) => p.propertyId === propertyIdFromUrl) || null;
          } else if (selectedProperty) {
            propertyToSelect = selectedProperty;
          }

          if (propertyToSelect) {
            dispatch(setSelectedProperty(propertyToSelect));
            (onPropertySelect as (id: string) => void)(
              propertyToSelect.propertyId
            );
            if (!propertyIdFromUrl) {
              setSearchParams((prev) =>
                updateSearchParam(
                  prev,
                  "propertyId",
                  propertyToSelect.propertyId
                )
              );
            }
          } else {
            setLoading(false);
          }
        }
      } catch (error) {
        showSnackbar("Failed to load properties. Please try again.", "error");
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [
    onPropertySelect,
    dispatch,
    setSearchParams,
    propertyIdFromUrl,
    selectedProperty,
    showSnackbar,
    page,
    hasAutoSelectedPortfolio,
    isInternal,
  ]);

  useEffect(() => {
    if (
      !multiSelect &&
      hasFetched.current &&
      propertyIdFromUrl &&
      properties.length > 0
    ) {
      const propertyToSelect = properties.find(
        (p) => p.propertyId === propertyIdFromUrl
      );
      if (
        propertyToSelect &&
        (!selectedProperty || selectedProperty.propertyId !== propertyIdFromUrl)
      ) {
        dispatch(setSelectedProperty(propertyToSelect));
        (onPropertySelect as (id: string) => void)(propertyToSelect.propertyId);
      }
    }
  }, [
    propertyIdFromUrl,
    properties,
    selectedProperty,
    dispatch,
    onPropertySelect,
    multiSelect,
  ]);

  // Sync local selectedProperties when URL changes externally (e.g. chip removal from the page)
  const propertyIdsKey = propertyIdsFromUrl.join(",");
  useEffect(() => {
    if (!multiSelect || !hasFetched.current || properties.length === 0) return;
    if (isPerPropertyRoute) return;
    if (!searchParams.has("propertyIds") && !searchParams.has("propertyId")) {
      return;
    }
    const urlIdSet = new Set(propertyIdsFromUrl);
    const localIdSet = new Set(selectedProperties.map((p) => p.propertyId));
    const same =
      urlIdSet.size === localIdSet.size &&
      propertyIdsFromUrl.every((id) => localIdSet.has(id));
    if (!same) {
      const matched = propertyIdsFromUrl
        .map((id) => properties.find((p) => p.propertyId === id))
        .filter((p): p is Property => p !== undefined);
      setSelectedProperties(matched);
      dispatch(
        matched.length > 0
          ? setSelectedMultiProperties(matched)
          : clearSelectedMultiProperties()
      );
    }
    // properties.length is intentionally included: when the fetch completes after
    // index.tsx has already restored propertyIds to the URL, this effect must
    // re-run to pick up those IDs (the key didn't change, but data is now available).
  }, [propertyIdsKey, properties.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── single-select handlers ────────────────────

  const handlePropertyChange = (newValue: Property | null) => {
    if (newValue === null) {
      dispatch(clearSelectedProperty());
      if (!isOnInstancePage)
        setSearchParams((prev) => updateSearchParam(prev, "propertyId", null));
    } else {
      dispatch(setSelectedProperty(newValue));
      if (!isOnInstancePage) {
        setSearchParams((prev) =>
          updateSearchParam(prev, "propertyId", newValue.propertyId)
        );
      }
      (onPropertySelect as (id: string) => void)(newValue.propertyId);
    }
  };

  // ── multi-select dropdown handlers ───────────

  const handleOpenDropdown = () => {
    setStableProperties(sortWithSelectedFirst(properties, selectedProperties));
    setPendingIds(new Set(selectedProperties.map((p) => p.propertyId)));
    setDropdownSearch("");
    setDropdownOpen(true);
  };

  // If the dropdown is opened before the property fetch resolves (slow
  // network, opened within the first render), stableProperties snapshots as
  // empty above and — without this — never gets a chance to pick up the data
  // once it arrives while the dropdown is still open, leaving the list stuck
  // empty until the user closes and reopens it.
  useEffect(() => {
    if (
      dropdownOpen &&
      stableProperties.length === 0 &&
      properties.length > 0
    ) {
      setStableProperties(
        sortWithSelectedFirst(properties, selectedProperties)
      );
    }
  }, [properties, dropdownOpen]);

  const handleCloseDropdown = () => {
    setDropdownOpen(false);
    setDropdownSearch("");
  };

  const handleTriggerClick = () =>
    dropdownOpen ? handleCloseDropdown() : handleOpenDropdown();

  const handleDesktopOpenChange = (next: boolean) => {
    if (next) handleOpenDropdown();
    else handleCloseDropdown();
  };

  const handleToggleProperty = (property: Property) => {
    setPendingIds((prev) => {
      if (!prev.has(property.propertyId) && prev.size >= MAX_PROPERTIES)
        return prev;
      const next = new Set(prev);
      if (next.has(property.propertyId)) {
        next.delete(property.propertyId);
      } else {
        next.add(property.propertyId);
      }
      return next;
    });
  };

  const handleSelectAllPortfolio = () => {
    if (isActiveLeadsMultiSelect) {
      trackActiveLeadsEvent(ACTIVE_LEADS_EVENTS.PropertySelectAllClicked, {
        property_ids: Array.from(portfolioPropertyIds),
        portfolio_size: portfolioPropertyIds.size,
        capped: portfolioPropertyIds.size > MAX_PROPERTIES,
      });
    }
    setPendingIds((prev) => {
      const next = new Set(prev);
      for (const p of filteredPortfolio) {
        if (next.size >= MAX_PROPERTIES) break;
        next.add(p.propertyId);
      }
      return next;
    });
  };

  const handleApply = () => {
    const newSelection = properties.filter((p) => pendingIds.has(p.propertyId));
    setSelectedProperties(newSelection);
    const ids = newSelection.map((p) => p.propertyId);

    if (isActiveLeadsMultiSelect && ids.length > 0) {
      const isPortfolioSelection =
        newSelection.length > 1 &&
        portfolioPropertyIds.size > 0 &&
        newSelection.length ===
          Math.min(portfolioPropertyIds.size, MAX_PROPERTIES) &&
        newSelection.every((p) => portfolioPropertyIds.has(p.propertyId));
      trackActiveLeadsEvent(ACTIVE_LEADS_EVENTS.PropertySelectionApplied, {
        property_ids: ids,
        property_count: ids.length,
        selection_type:
          ids.length === 1
            ? "single"
            : isPortfolioSelection
            ? "portfolio_all"
            : "multiple",
      });
    }

    setSearchParams((prev) =>
      updateSearchParam(
        prev,
        "propertyIds",
        ids.length > 0 ? ids.join(",") : null
      )
    );
    if (ids.length > 0) {
      dispatch(setSelectedMultiProperties(newSelection));
    } else {
      dispatch(clearSelectedMultiProperties());
    }
    (onPropertySelect as (ids: string[]) => void)(ids);
    setDropdownOpen(false);
    setDropdownSearch("");
  };

  // ── multi-select trigger label ────────────────

  const isMyPortfolioSelection =
    multiSelect &&
    selectedProperties.length > 1 &&
    portfolioPropertyIds.size > 0 &&
    selectedProperties.length ===
      Math.min(portfolioPropertyIds.size, MAX_PROPERTIES) &&
    selectedProperties.every((p) => portfolioPropertyIds.has(p.propertyId));

  const triggerLabel =
    selectedProperties.length === 0
      ? null
      : selectedProperties.length === 1
      ? selectedProperties[0].propertyName ||
        selectedProperties[0].propertyId ||
        "Unknown Property"
      : isMyPortfolioSelection
      ? `My Portfolio Selected (${selectedProperties.length}${
          portfolioPropertyIds.size > MAX_PROPERTIES
            ? ` of ${portfolioPropertyIds.size}`
            : ""
        })`
      : `Multiple Properties Selected (${selectedProperties.length})`;

  // portfolioList (the FULL portfolio, not narrowed by search) feeds
  // filteredPortfolio below.
  const portfolioList = useMemo(
    () =>
      multiSelect && portfolioPropertyIds.size > 0
        ? stableProperties.filter((p) => portfolioPropertyIds.has(p.propertyId))
        : ([] as Property[]),
    [multiSelect, portfolioPropertyIds, stableProperties]
  );

  const { filteredPortfolio, filteredNonPortfolio } = useMemo(() => {
    const matchSearch = (p: Property) =>
      !dropdownSearch ||
      p.propertyName.toLowerCase().includes(dropdownSearch.toLowerCase()) ||
      p.propertyId.toLowerCase().includes(dropdownSearch.toLowerCase());

    if (multiSelect && portfolioPropertyIds.size > 0) {
      return {
        filteredPortfolio: portfolioList.filter(matchSearch),
        filteredNonPortfolio: stableProperties.filter(
          (p) => !portfolioPropertyIds.has(p.propertyId) && matchSearch(p)
        ),
      };
    }

    return {
      filteredPortfolio: [] as Property[],
      filteredNonPortfolio: stableProperties.filter(matchSearch),
    };
  }, [
    multiSelect,
    portfolioPropertyIds,
    stableProperties,
    dropdownSearch,
    portfolioList,
  ]);

  const allFilteredPortfolioSelected = useMemo(
    () =>
      filteredPortfolio.length > 0 &&
      filteredPortfolio.every((p) => pendingIds.has(p.propertyId)),
    [filteredPortfolio, pendingIds]
  );

  // Computed once here instead of duplicated in both MultiSelectDesktopDropdown
  // and MultiSelectMobileSheet.
  const showSelectAllPortfolio =
    filteredPortfolio.length > 0 &&
    !allFilteredPortfolioSelected &&
    pendingIds.size < MAX_PROPERTIES;

  const isChecked = (property: Property) => pendingIds.has(property.propertyId);

  const selectionLimitWarning =
    pendingIds.size >= MAX_PROPERTIES ? (
      <SelectionLimitWarning max={MAX_PROPERTIES} />
    ) : null;

  // ── render ────────────────────────────────────

  return (
    <div className="ml-0.5 min-w-0 md:ml-2 md:min-w-[400px]">
      {multiSelect ? (
        darkBackground ? (
          <MultiSelectMobileSheet
            loading={loading}
            triggerLabel={triggerLabel}
            dropdownOpen={dropdownOpen}
            onTriggerClick={handleTriggerClick}
            onClose={handleCloseDropdown}
            dropdownSearch={dropdownSearch}
            onSearchChange={setDropdownSearch}
            filteredPortfolio={filteredPortfolio}
            filteredNonPortfolio={filteredNonPortfolio}
            showSelectAllPortfolio={showSelectAllPortfolio}
            pendingCount={pendingIds.size}
            isChecked={isChecked}
            onToggleProperty={handleToggleProperty}
            onSelectAllPortfolio={handleSelectAllPortfolio}
            onClearPending={() => setPendingIds(new Set())}
            onApply={handleApply}
            selectionLimitWarning={selectionLimitWarning}
          />
        ) : (
          <MultiSelectDesktopDropdown
            loading={loading}
            triggerLabel={triggerLabel}
            dropdownOpen={dropdownOpen}
            onOpenChange={handleDesktopOpenChange}
            dropdownSearch={dropdownSearch}
            onSearchChange={setDropdownSearch}
            filteredPortfolio={filteredPortfolio}
            filteredNonPortfolio={filteredNonPortfolio}
            showSelectAllPortfolio={showSelectAllPortfolio}
            pendingCount={pendingIds.size}
            isChecked={isChecked}
            onToggleProperty={handleToggleProperty}
            onSelectAllPortfolio={handleSelectAllPortfolio}
            onClearPending={() => setPendingIds(new Set())}
            onApply={handleApply}
            selectionLimitWarning={selectionLimitWarning}
          />
        )
      ) : (
        <SingleSelectPropertyPanel
          properties={properties}
          portfolioPropertyIds={portfolioPropertyIds}
          loading={loading}
          selectedProperty={selectedProperty}
          onChange={handlePropertyChange}
        />
      )}
    </div>
  );
};

export default PropertySelector;
